import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApexChart from "react-apexcharts";
import CountUp from "react-countup";
import Skeleton from "react-loading-skeleton";
import FeatherIcon from "feather-icons-react";
import { FaClipboardList } from "react-icons/fa";
import toastr from "toastr";
import BrokerEmptyState from "../../components/Broker/BrokerEmptyState";
import { buildBrokerPath, useBrokerBasePath } from "../../components/Broker/brokerPaths";
import { BrokerService } from "./BrokerService";
import {
  EMPTY_STATUS_STATS,
  TREND_RANGES,
  buildTrendSeries,
  getTrendRange,
  parseKycDueFromSteps,
  parseStatusStats,
  rangeWindow,
} from "./brokerDashboardStats";
import { getBrokerSystemTagId, recordBrokerVisibleSystemTagElements } from "./brokerSystemTags";

// KYC Due = applications whose KYC Verification step is Draft (0) or Incomplete (2).
// Count comes from a steps-aggregation; the deep-link selects the "KYC Due" status
// option (which the applications page maps to the same kycStepStatus filter).
const KYC_DUE_QUERY = "status=KYC_DUE";
// Missing Info / KYC Failed deep-link via the system-tag filter so the list — and the
// card's tag-based count — reflect the FV Bank-stamped tag rather than a status proxy
// (the overall status is masked to Draft while later steps are unreached).
const MISSING_INFO_QUERY = "systemTag=MISSING_INFO";
const KYC_FAILED_QUERY = "systemTag=KYC_FAILED";

// Pipeline states a broker actually acts on, in priority order.
const PIPELINE_BARS = [
  {
    key: "missingInfo",
    label: "Missing Information",
    shortLabel: "Missing info",
    help: "FV Bank requested more information.",
    icon: "message-square",
    tone: "info",
    query: MISSING_INFO_QUERY,
  },
  {
    key: "kycFailed",
    label: "KYC Failed",
    shortLabel: "KYC failed",
    help: "Individual verification failed and needs a resend.",
    icon: "alert-triangle",
    tone: "danger",
    query: KYC_FAILED_QUERY,
  },
  {
    key: "kycDue",
    label: "KYC Due",
    shortLabel: "KYC due",
    help: "Verification links are pending completion.",
    icon: "shield",
    tone: "neutral",
    query: KYC_DUE_QUERY,
  },
  {
    key: "incomplete",
    // Mirrors the top "Incomplete" metric: draft + needs-info (status 0/2).
    label: "Incomplete",
    shortLabel: "Incomplete",
    help: "Applications still being prepared or needing more information.",
    icon: "edit-3",
    tone: "dark",
    query: "status=INCOMPLETE",
  },
];

const PIPELINE_COLORS = {
  info: "#0e7490",
  danger: "#780905",
  neutral: "#64748b",
  dark: "#061937",
};

const BrokerDashboard = () => {
  const navigate = useNavigate();
  const brokerBasePath = useBrokerBasePath();

  const [statusStats, setStatusStats] = useState(EMPTY_STATUS_STATS);
  // KYC Due count = apps whose KYC Verification step is status 0 or 2.
  const [kycDue, setKycDue] = useState(0);
  // Tag-based pipeline counts (Missing Info / KYC Failed) so each card's number
  // equals the system-tag list it deep-links to.
  const [tagCounts, setTagCounts] = useState({ missingInfo: 0, kycFailed: 0 });
  const [trend, setTrend] = useState({ labels: [], counts: [], total: 0 });
  const [trendRange, setTrendRange] = useState("3m");
  const [trendLoading, setTrendLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  // Links carry the new simplified-filter query (status=… / kycStatus=…) since
  // the legacy ?view= deep-links are no longer used by the applications page.
  const goToApplications = useCallback((query) => {
    navigate(`${buildBrokerPath(brokerBasePath, "/applications")}${query ? `?${query}` : ""}`);
  }, [brokerBasePath, navigate]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      BrokerService.statistics({ groupBy: { status: "status" } }),
      // KYC Due = apps whose KYC Verification step is Draft (0) / Incomplete (2). Unwind
      // steps + group by (label, status); summing the "KYC Verification" 0/2 rows is exact
      // (each step keeps its status after unwind). notInFilters scopes it to live apps —
      // excludes Onboarded (6) / Declined (8) / Archived (9); Rejected (4) is kept. Mirrors
      // the ?status=KYC_DUE list (kycStepStatus=0,2 + status=notin[6,8,9]), so count == list.
      BrokerService.statistics({
        unwind: "steps",
        filters: { "steps.label": "KYC Verification" },
        inFilters: { "steps.status": [0, 2] },
        notInFilters: { status: [6, 8, 9] },
        groupBy: { label: "steps.label", status: "steps.status" },
      }).catch(() => null),
      BrokerService.applicationSystemTagElements().catch(() => null),
    ])
      .then(async ([statusRes, kycDueRes, tagRes]) => {
        if (!mounted) return;
        setStatusStats(parseStatusStats(statusRes?.data));
        setKycDue(parseKycDueFromSteps(kycDueRes?.data));
        recordBrokerVisibleSystemTagElements(tagRes?.data?.rows || []);
        const missingInfoId = getBrokerSystemTagId("missing_info");
        const kycFailedId = getBrokerSystemTagId("kyc_failed");
        // Missing Info / KYC Failed counts via grid totals — the SAME filter their cards
        // deep-link to (tag_id), so count == list. (KYC Due's count above and its
        // ?status=KYC_DUE deep-link both resolve to "KYC Verification step in 0/2".)
        const [miRes, kfRes] = await Promise.all([
          missingInfoId ? BrokerService.applications({ page: 1, rows: 1, tag_ids: missingInfoId }).catch(() => null) : null,
          kycFailedId ? BrokerService.applications({ page: 1, rows: 1, tag_ids: kycFailedId }).catch(() => null) : null,
        ]);
        if (!mounted) return;
        setTagCounts({
          missingInfo: Number(miRes?.data?.total || 0),
          kycFailed: Number(kfRes?.data?.total || 0),
        });
      })
      .catch((error) => toastr.error(error?.message || "Unable to load dashboard"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  // The onboarding-volume trend is dynamic: changing the range refetches and
  // re-buckets (daily for 30D, monthly for longer windows).
  useEffect(() => {
    let mounted = true;
    setTrendLoading(true);
    const [from, to] = rangeWindow(trendRange);
    BrokerService.statistics({ groupBy: { createdAt: "createdAt" }, btwFilters: { createdAt: [from, to] } })
      .then((res) => { if (mounted) setTrend(buildTrendSeries(res?.data, trendRange)); })
      .catch(() => { if (mounted) setTrend({ labels: [], counts: [], total: 0 }); })
      .finally(() => mounted && setTrendLoading(false));
    return () => { mounted = false; };
  }, [trendRange]);

  const metrics = [
    { key: "total", label: "Total applications", value: statusStats.total, icon: "layers", tone: "brand", query: "" },
    { key: "incomplete", label: "Incomplete", value: statusStats.draft + statusStats.needsInfo, icon: "edit-3", tone: "slate", query: "status=INCOMPLETE" },
    { key: "submitted", label: "Submitted", value: statusStats.submitted + statusStats.inReview + statusStats.approved, icon: "send", tone: "indigo", query: "status=SUBMITTED" },
    { key: "onboarded", label: "Onboarded", value: statusStats.provisioned + statusStats.expired, icon: "check-circle", tone: "green", query: "status=ONBOARDED" },
  ];

  const pipeline = useMemo(() => {
    const values = {
      // Each card's count matches its deep-linked list: Missing Info / KYC Failed are
      // tag-based; KYC Due is the KYC Verification step (status 0/2); Incomplete mirrors
      // the top "Incomplete" metric (draft + needs-info, status 0/2).
      missingInfo: tagCounts.missingInfo,
      kycFailed: tagCounts.kycFailed,
      kycDue,
      incomplete: statusStats.draft + statusStats.needsInfo,
    };
    const items = PIPELINE_BARS.map((bar) => ({ ...bar, value: values[bar.key] || 0 }));
    const total = items.reduce((sum, item) => sum + item.value, 0);
    const max = Math.max(...items.map((item) => item.value), 1);
    return {
      total,
      hasData: total > 0,
      max,
      items: items.map((item) => ({
        ...item,
        share: total ? Math.round((item.value / total) * 100) : 0,
      })),
    };
  }, [statusStats, kycDue, tagCounts]);

  // Pipeline bar chart — same package (ApexCharts) as the "New applications" graph.
  const pipelineChart = useMemo(() => ({
    series: [{ name: "Open items", data: pipeline.items.map((item) => item.value) }],
    options: {
      chart: {
        type: "bar",
        toolbar: { show: false },
        fontFamily: "inherit",
        animations: { enabled: true, speed: 450 },
        events: {
          dataPointSelection: (_event, _ctx, config) => {
            const item = pipeline.items[config?.dataPointIndex];
            if (item) goToApplications(item.query);
          },
        },
      },
      colors: pipeline.items.map((item) => PIPELINE_COLORS[item.tone] || PIPELINE_COLORS.dark),
      plotOptions: { bar: { distributed: true, borderRadius: 8, borderRadiusApplication: "end", columnWidth: "50%" } },
      dataLabels: { enabled: false },
      legend: { show: false },
      grid: { borderColor: "rgba(0, 0, 0, 0.06)", strokeDashArray: 4, padding: { left: 8, right: 8 } },
      xaxis: {
        categories: pipeline.items.map((item) => item.shortLabel || item.label),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: "#64748b", fontSize: "12px", fontWeight: 700 } },
      },
      yaxis: {
        min: 0,
        max: Math.max(pipeline.max, 4),
        forceNiceScale: true,
        labels: { style: { colors: "#94a3b8", fontSize: "11px" }, formatter: (value) => String(Math.round(value)) },
      },
      states: { active: { filter: { type: "none" } }, hover: { filter: { type: "darken", value: 0.9 } } },
      tooltip: {
        y: {
          formatter: (value, opts) => {
            const item = pipeline.items[opts?.dataPointIndex];
            return item ? `${value} item${value === 1 ? "" : "s"} · ${item.share}% of open pipeline` : `${value}`;
          },
        },
      },
    },
  }), [goToApplications, pipeline]);

  const area = useMemo(() => ({
    hasData: trend.counts.some((value) => value > 0),
    series: [{ name: "New applications", data: trend.counts }],
    options: {
      chart: { type: "area", toolbar: { show: false }, sparkline: { enabled: false }, animations: { enabled: true, speed: 700 }, fontFamily: "inherit" },
      colors: ["#041936"],
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 3 },
      fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 100] } },
      grid: { borderColor: "rgba(148,163,184,0.18)", strokeDashArray: 4, padding: { left: 8, right: 8 } },
      xaxis: { categories: trend.labels, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#94a3b8", fontSize: "12px" } } },
      yaxis: { labels: { style: { colors: "#94a3b8", fontSize: "12px" }, formatter: (value) => String(Math.round(value)) }, min: 0, forceNiceScale: true },
      tooltip: { y: { formatter: (value) => `${value} application${value === 1 ? "" : "s"}` } },
    },
  }), [trend]);

  return (
    <div className="broker-page broker-dashboard-page">
      <section className="broker-metric-grid" aria-label="Pipeline summary">
        {metrics.map((metric, index) => (
          <button
            key={metric.key}
            type="button"
            className={`broker-metric broker-metric--${metric.tone} broker-rise`}
            style={{ animationDelay: `${index * 70}ms` }}
            onClick={() => goToApplications(metric.query)}
          >
            <span className="broker-metric__icon"><FeatherIcon icon={metric.icon} size={20} /></span>
            <span className="broker-metric__value">
              {loading ? <Skeleton width={52} height={30} /> : <CountUp end={metric.value} duration={1.1} separator="," />}
            </span>
            <span className="broker-metric__label">{metric.label}</span>
          </button>
        ))}
      </section>

      <section className="broker-dashboard-columns">
        <div className="broker-panel broker-chart-card broker-rise" style={{ animationDelay: "120ms" }}>
          <div className="broker-panel__header">
            <div>
              <h2>Pipeline status</h2>
            </div>
          </div>
          {loading ? (
            <div className="broker-pipeline-skeleton">
              <Skeleton height={52} count={4} />
            </div>
          ) : pipeline.hasData ? (
            <div className="broker-pipeline-card" aria-label="Pipeline status breakdown">
              <div className="broker-pipeline-chart" aria-label={`Open pipeline chart with ${pipeline.total} open item${pipeline.total === 1 ? "" : "s"}.`}>
                <ApexChart options={pipelineChart.options} series={pipelineChart.series} type="bar" height="100%" />
              </div>
              <div className="broker-pipeline-legend" aria-label="Pipeline status details">
                {pipeline.items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`broker-pipeline-legend__item broker-pipeline-legend__item--${item.tone}`}
                    onClick={() => goToApplications(item.query)}
                  >
                    <span className="broker-pipeline-legend__dot" aria-hidden="true" />
                    <span>
                      <strong>{item.label}</strong>
                      <small>{item.value > 0 ? `${item.value} waiting` : "None waiting"}</small>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <BrokerEmptyState icon={FaClipboardList} title="Nothing in the pipeline" description="Missing information, KYC issues and drafts will appear here as your applications progress." />
          )}
        </div>

        <div className="broker-panel broker-chart-card broker-rise" style={{ animationDelay: "180ms" }}>
          <div className="broker-panel__header">
            <div>
              <h2>New applications</h2>
              <p className="broker-panel__subtitle">
                {trend.total
                  ? `${trend.total} created in the ${getTrendRange(trendRange).label === "30D" ? "last 30 days" : `last ${getTrendRange(trendRange).label}`}`
                  : "Onboarding volume over time."}
              </p>
            </div>
            <div className="broker-range-toggle" role="tablist" aria-label="Trend range">
              {TREND_RANGES.map((range) => (
                <button
                  key={range.key}
                  type="button"
                  role="tab"
                  aria-selected={trendRange === range.key}
                  className={`broker-range-toggle__option ${trendRange === range.key ? "is-active" : ""}`}
                  onClick={() => setTrendRange(range.key)}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
          {trendLoading ? (
            <div className="broker-chart-skeleton"><Skeleton height={210} /></div>
          ) : trend.total ? (
            <ApexChart options={area.options} series={area.series} type="area" height={240} />
          ) : (
            <BrokerEmptyState
              icon={FaClipboardList}
              title="No onboarding volume yet"
              description="Once you start creating applications, your onboarding trend will appear here. Try a wider range to see earlier activity."
            />
          )}
        </div>
      </section>

    </div>
  );
};

export default BrokerDashboard;
